import { usesHostedBilling, usesStoreBilling } from '@/lib/appDistribution';
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { View, Platform } from 'react-native';
import { StyleSheet } from 'react-native';
import ModalLayout from './ModalLayout';
import PricingUpgrade from '@/components/PricingUpgrade';
import { useAuthStore, useModalStore } from '@/store';
import { useRouter } from 'expo-router';
import { trackEvent } from '@/constants/utils/analytics';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { checkIsIapSubscriber, hasValidPlusAccess } from '@/lib/purchaseUtils';
import useResponsiveness from '@/hooks/useResponsiveness';

const MODAL_WIDTH = 560;
// Footer CTA row in ModalLayout (padding + one large button)
const LOWER_ACTIONS_HEIGHT = 72;
const MODAL_HEIGHT_PAD = 4;
const MODAL_HEIGHT_MIN = 480;
const MODAL_HEIGHT_MIN_MOBILE = 640;
const MODAL_HEIGHT_MAX = 900;

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
});

function PricingModal({ onClose, title, subtitle, onLowerActionsLayout, promoCode, onDimensionsChange, initialCadence, skipLabel, onSkip, onboarding = false }) {
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const isComped = useAuthStore(state => state.user?.flags?.isComped);
  const plusExpiresAt = useAuthStore(state => state.user?.plusExpiresAt);
  const activePurchase = useAuthStore(state => state.user?.activePurchase);
  const { isXS } = useResponsiveness();
  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(activePurchase),
    [activePurchase],
  );
  const hasValidPlus = useMemo(
    () => hasValidPlusAccess({ isPlus, isComped, plusExpiresAt, purchase: activePurchase }),
    [isPlus, isComped, plusExpiresAt, activePurchase],
  );
  const { openModal } = useModalStore(state => state.actions);
  const router = useRouter();
  const pricingRef = useRef();
  const lastHeightRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const isNative = usesStoreBilling();
  // Plus Premium is web-only and must not stack on App Store / Play Plus.
  // Expired / lapsed Plus (stale flag, retired Paddle) must still see the
  // regular Plus card so they can resubscribe — not only Plus Premium.
  const upgradingToPremium = hasValidPlus && !isPlusPremium && usesHostedBilling() && !isIapSubscriber;
  const [ctaLabel, setCtaLabel] = useState('Continue to Payment');
  const handleSelectionChange = useCallback((selection) => {
    if (selection?.ctaLabel) setCtaLabel(selection.ctaLabel);
  }, []);

  const defaultSubtitle = useMemo(() => {
    if (upgradingToPremium) {
      return 'Run up to 10 Work sessions at once, up from 3 on Plus. Get 6× more credits, too.';
    }
    if (isXS) {
      return `Work mode — an AI agent with its own computer. Plus includes free default-model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} credits/mo.`;
    }
    return `Unlock Work mode—AI agents that plan, research, use tools, and deliver finished work. Plus also includes free default model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`;
  }, [upgradingToPremium, isXS]);

  const displaySubtitle = subtitle || defaultSubtitle;

  const handleContentLayout = useCallback((event) => {
    const contentH = event?.nativeEvent?.layout?.height;
    if (!contentH || !onDimensionsChange) return;
    const minH = isXS ? MODAL_HEIGHT_MIN_MOBILE : MODAL_HEIGHT_MIN;
    const actionsHeight = skipLabel && onSkip ? 124 : LOWER_ACTIONS_HEIGHT;
    const next = Math.min(
      MODAL_HEIGHT_MAX,
      Math.max(minH, Math.ceil(contentH + actionsHeight + MODAL_HEIGHT_PAD)),
    );
    // Avoid noisy re-layouts from sub-pixel churn
    if (Math.abs(next - lastHeightRef.current) < 8) return;
    lastHeightRef.current = next;
    onDimensionsChange({
      width: MODAL_WIDTH,
      height: next,
      // Prefer content height on mobile; soft-cap keeps short iPhones usable.
      heightMobile: [next, '96%'],
    });
  }, [onDimensionsChange, isXS, skipLabel, onSkip]);

  // On native platforms, redirect to the dedicated IAP paywall screen.
  // `onClose` / `router` stay out of the deps: they are unstable identities and
  // the guard below stays true after navigating, so including them re-pushes
  // the paywall on every parent re-render.
  useEffect(() => {
    if (isNative && !isGhost && !hasValidPlus) {
      onClose?.();
      router.push('/(auth)/paywall');
    }
  }, [isNative, isGhost, hasValidPlus]);

  // One conversion event per open. `hasValidPlus` settles a render or two later
  // (activePurchase loads async), so this cannot key off the deps alone.
  const trackedPricingViewRef = useRef(false);
  useEffect(() => {
    if (trackedPricingViewRef.current) return;
    if (!isGhost && !hasValidPlus && !isNative) {
      trackedPricingViewRef.current = true;
      trackEvent('conversion', 'viewed_pricing', 'pricing_modal');
    }
  }, [isGhost, hasValidPlus, isNative]);

  // Hooks must stay unconditional: the early returns below flip as
  // isPlus/isPlusPremium change (e.g. right after a successful Premium
  // upgrade while the modal is still mounted), and skipping this hook would
  // crash with a hook-count mismatch.
  const handleUpgrade = useCallback(async () => {
    if (isLoading) return;

    if (pricingRef.current) {
      setIsLoading(true);
      try {
        const success = await pricingRef.current.handleUpgrade();
        if (success) {
          onClose?.();
        }
      } finally {
        setIsLoading(false);
      }
    }
  }, [isLoading, onClose, router]);

  if (isNative && !isGhost && !hasValidPlus) {
    return null;
  }

  if (isGhost) {
    !!onClose && onClose();
    openModal('GhostSignupModal');
    return null;
  }

  // Current Plus (non-Premium) web users can open this to pick Plus Premium —
  // except App Store / Google Play subscribers (stacking is blocked server-side
  // too). Native / already-Premium / IAP users have nothing to buy here.
  // Lapsed Plus must not take this exit — they need the Plus card to resubscribe.
  if (hasValidPlus && (isNative || isPlusPremium || isIapSubscriber)) {
    !!onClose && onClose();
    return null;
  }

  const lowerActions = [
    {
      title: ctaLabel,
      onPress: handleUpgrade,
      primary: true,
      variant: onboarding ? 'primary' : 'plus',
      ...(onboarding ? { style: { backgroundColor: '#1D1D1F', borderRadius: 16 }, textStyle: { color: '#FFFFFF' } } : {}),
      disabled: isLoading,
      isLoading: isLoading,
      textWeight: 'bold'
    },
    ...(skipLabel && onSkip ? [{
      title: skipLabel,
      ...(onboarding ? { style: { backgroundColor: '#F5F5F7', borderRadius: 16 }, textStyle: { color: '#1D1D1F' } } : {}),
      onPress: () => { onClose?.(); onSkip(); },
      disabled: isLoading,
    }] : []),
  ];

  return (
    <ModalLayout
      hideCloseButton={true}
      onClose={onClose}
      titleBorder={false}
      background='primary'
      lowerActions={lowerActions}
      onLowerActionsLayout={onLowerActionsLayout}
    >
      <View style={styles.container} onLayout={handleContentLayout}>
        <PricingUpgrade 
          ref={pricingRef}
          onboarding={onboarding}
          withPadding={true}
          withScroll={false}
          title={title || (upgradingToPremium ? 'Upgrade to Plus Premium' : 'Unlock Work mode with Gab AI Plus')}
          subtitle={displaySubtitle}
          promoCode={promoCode}
          onSelectionChange={handleSelectionChange}
          defaultTier={upgradingToPremium ? 'premium' : 'plus'}
          initialCadence={initialCadence}
          onDismiss={onClose}
          hidePlusCard={upgradingToPremium}
        />
      </View>
    </ModalLayout>
  );
} 

// Initial size before onLayout measures the real content height.
// Mobile web uses most of the viewport so plan cards stay readable.
PricingModal.modalOptions = {
  width: MODAL_WIDTH,
  height: 780,
  heightMobile: [820, '96%'],
  background: 'primary',
};

export default PricingModal;
